import React from 'react';

/**
 * Reusable Skeleton Component for Loading States
 */

// Base Skeleton
export const Skeleton = ({ className = '', width = 'w-full', height = 'h-4' }) => {
    return (
        <div
            className={`${width} ${height} bg-gray-700/50 rounded animate-pulse ${className}`}
        />
    );
};

// Skeleton for Table Row
export const TableRowSkeleton = () => {
    return (
        <tr className="border-b border-border-gray">
            <td className="px-6 py-4">
                <Skeleton width="w-24" height="h-4" />
            </td>
            <td className="px-6 py-4">
                <Skeleton width="w-32" height="h-4" />
            </td>
            <td className="px-6 py-4">
                <Skeleton width="w-40" height="h-4" />
            </td>
            <td className="px-6 py-4">
                <Skeleton width="w-28" height="h-4" />
            </td>
            <td className="px-6 py-4">
                <Skeleton width="w-32" height="h-4" />
            </td>
            <td className="px-6 py-4">
                <Skeleton width="w-20" height="h-4" />
            </td>
            <td className="px-6 py-4">
                <Skeleton width="w-28" height="h-6" />
            </td>
            <td className="px-6 py-4">
                <Skeleton width="w-24" height="h-4" />
            </td>
        </tr>
    );
};

// Skeleton for Card
export const CardSkeleton = () => {
    return (
        <div className="bg-dark-card border border-border-gray rounded-xl p-6">
            <div className="flex items-start gap-4">
                <Skeleton width="w-12" height="h-12" className="rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-3">
                    <Skeleton width="w-32" height="h-4" />
                    <Skeleton width="w-full" height="h-3" />
                    <Skeleton width="w-3/4" height="h-3" />
                </div>
            </div>
        </div>
    );
};

// Skeleton for Project Status Card
export const ProjectStatusSkeleton = () => {
    return (
        <div className="bg-dark-card border border-border-gray rounded-xl p-6">
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <Skeleton width="w-40" height="h-6" />
                    <Skeleton width="w-24" height="h-6" className="rounded-full" />
                </div>
                <div className="space-y-2">
                    <Skeleton width="w-full" height="h-4" />
                    <Skeleton width="w-3/4" height="h-4" />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border-gray">
                    <div>
                        <Skeleton width="w-20" height="h-3" className="mb-2" />
                        <Skeleton width="w-32" height="h-4" />
                    </div>
                    <div>
                        <Skeleton width="w-20" height="h-3" className="mb-2" />
                        <Skeleton width="w-28" height="h-4" />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Skeleton for Admin Dashboard Stats
export const StatCardSkeleton = () => {
    return (
        <div className="bg-dark-card border border-border-gray rounded-xl p-6">
            <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                    <Skeleton width="w-24" height="h-3" />
                    <Skeleton width="w-16" height="h-8" />
                </div>
                <Skeleton width="w-12" height="h-12" className="rounded-full" />
            </div>
        </div>
    );
};

// Skeleton for Full Submissions Table
export const SubmissionsTableSkeleton = () => {
    return (
        <div className="space-y-6">
            {/* Header Skeleton */}
            <div className="flex items-center justify-between">
                <div>
                    <Skeleton width="w-48" height="h-8" className="mb-2" />
                    <Skeleton width="w-32" height="h-4" />
                </div>
                <Skeleton width="w-28" height="h-10" className="rounded-lg" />
            </div>

            {/* Search Skeleton */}
            <div className="bg-dark-card border border-border-gray rounded-xl p-4">
                <Skeleton width="w-full" height="h-5" />
            </div>

            {/* Table Skeleton */}
            <div className="bg-dark-card border border-border-gray rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-dark-bg border-b border-border-gray">
                            <tr>
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                    <th key={i} className="px-6 py-4">
                                        <Skeleton width="w-20" height="h-3" />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-gray">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <TableRowSkeleton key={i} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Skeleton;
